import React, { useState, useEffect } from "react";
import "../../../css/Marketing/AddMarketingItems.css";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import { db, auth } from "../../../backend/firebaseConfig";
import { getStorage, ref, listAll, getDownloadURL } from "firebase/storage";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";
import NewInputField from "../../DesignSystem/Inputs/NewInputField";
import NewTextArea from "../../DesignSystem/Inputs/NewTextArea";
import CustomSelect from "../../DesignSystem/Dropdowns/CustomSelect";

const AddBlog = () => {
  // Use state for newBlog so React tracks changes
  const [newBlog, setNewBlog] = useState({
    // title: "",
    link: "",
    content: "",
    image: "",
  });

  const [errors, setErrors] = useState({});
  const [images, setImages] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const { userPermissions } = useUserPermissions();
  const [isEditing, setIsEditing] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  useEffect(() => {
    fetchImages();
    fetchBlogs();
  }, []);

  useEffect(() => {
    if (images.length > 0 && !newBlog.image) {
      setNewBlog((prev) => ({
        ...prev,
        image: images[0].url,
      }));
    }
  }, [images]);

  const fetchBlogs = async () => {
    const docRef = doc(db, "Marketing", "Blogs");
    const docSnap = await getDoc(docRef);

    setBlogs(docSnap.exists() ? docSnap.data().items : []);
    if (docSnap.exists()) {
      // console.log("Blogs:", docSnap.data().items); // Logs the array of blogs
    } else {
      // console.log("No blogs found");
    }
  };

  const fetchImages = async () => {
    const storage = getStorage();
    const imagesRef = ref(storage, "Marketing/Blogs & Media/"); // Folder path

    try {
      const result = await listAll(imagesRef);
      const imageList = await Promise.all(
        result.items.map(async (item) => {
          const fullName = item.name; // Full filename with extension
          const nameWithoutExt = fullName.replace(/\.[^/.]+$/, ""); // Remove extension
          return {
            name: nameWithoutExt, // Filename without extension
            url: await getDownloadURL(item), // Full image URL
          };
        }),
      );
      setImages(imageList); // Store {name, url} pairs in state
    } catch (error) {
      // console.error("Error fetching images:", error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newBlog.content.trim()) newErrors.content = "Content is required";
    if (!newBlog.link.trim()) newErrors.link = "Link is required";
    if (!newBlog.image.trim()) newErrors.image = "Image is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (validateForm()) {
      try {
        const docRef = doc(db, "Marketing", "Blogs");
        const docSnap = await getDoc(docRef);
        let updatedBlogs = docSnap.exists() ? docSnap.data().items : [];

        if (isEditing && editIndex !== null) {
          updatedBlogs[editIndex] = {
            ...newBlog,
            postedOn: new Date(),
            postedBy:
              auth.currentUser.displayName || auth.currentUser.email || "",
          };
        } else {
          updatedBlogs = [
            {
              ...newBlog,
              postedOn: new Date(),
              postedBy:
                auth.currentUser.displayName || auth.currentUser.email || "",
            },
            ...updatedBlogs,
          ];
        }

        await setDoc(docRef, { items: updatedBlogs }, { merge: true });

        // console.log(isEditing ? "Blog Updated" : "Blog Added");
        fetchBlogs();

        setNewBlog({ link: "", content: "", image: images[0]?.url || "" });
        setIsEditing(false);
        setEditIndex(null);
      } catch (error) {
        // console.error("Error updating blogs: ", error);
      }
    }
  };

  return (
    <div className="AddMarketingItems-Column">
      <form className="marketing-add-form" onSubmit={handleSubmit}>
        <div className="fp-form-container">
          <h3 className="FP_Content_SemiBold">Add Blog</h3>
          <NewTextArea
            id="blog-content"
            label="Content"
            placeholder="Content"
            required
            error={errors.content}
            value={newBlog.content}
            onChange={(event) =>
              setNewBlog((previous) => ({
                ...previous,
                content: event.target.value,
              }))
            }
            maxLength={10000}
          />
          <NewInputField
            id="blog-link"
            label="Link"
            placeholder="Link"
            required
            error={errors.link}
            value={newBlog.link}
            onChange={(event) =>
              setNewBlog((previous) => ({
                ...previous,
                link: event.target.value,
              }))
            }
          />

          <div className="fp-form-column">
            <CustomSelect
              label="Select an Image"
              required
              options={images.map((image) => ({
                id: image.url,
                name: image.name,
              }))}
              value={newBlog.image}
              onChange={(event) =>
                setNewBlog((previous) => ({
                  ...previous,
                  image: event.target.value,
                }))
              }
              placeholder="Select an image"
              error={!!errors.image}
              clearable={false}
            />
            {errors.image && (
              <div className="fp-form-error FP_Content">{errors.image}</div>
            )}
          </div>

          <div className="fp-form-actions">
            <PrimaryButton label="Post" />
          </div>
        </div>
      </form>

      <table className="marketing-content-table">
        <thead>
          <tr>
            <th>
              <div className="FP_Content"></div>
            </th>

            {/* <th>
              <div className="Content-Large">Title</div>
            </th> */}
            <th>
              <div className="FP_Content">Content</div>
            </th>
            <th>
              <div className="FP_Content">Image</div>
            </th>
            <th>
              <div className="FP_Content">Link</div>
            </th>

            {userPermissions.includes("Administrator") && (
              <>
                <th>
                  <div className="FP_Content">Posted on</div>
                </th>
                <th>
                  <div className="FP_Content">Posted By</div>
                </th>
              </>
            )}

            <th>
              <div className="FP_Content">Edit</div>
            </th>
          </tr>
        </thead>

        <tbody>
          {blogs?.map((blog, index) => (
            <tr key={index}>
              <td>
                <div className="FP_Content text-black">{index + 1}.</div>
              </td>
              {/* <td>{blog.title}</td> */}
              <td className="item-content">
                <div className="FP_Content text-black">{blog.content}</div>
              </td>
              <td>
                {blog.image ? (
                  <a
                    href={blog.image}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="FP_Content text-primary-1 no-text-wrap">
                      View Image
                    </div>
                  </a>
                ) : (
                  "No Image"
                )}
              </td>
              <td>
                <a href={blog.link} target="_blank" rel="noopener noreferrer">
                  <div className="FP_Content text-primary-1">View</div>
                </a>
              </td>
              {userPermissions.includes("Administrator") && (
                <>
                  <td>
                    <div className="FP_Content text-black">
                      {new Date(
                        blog.postedOn.seconds * 1000,
                      ).toLocaleDateString()}
                    </div>
                  </td>
                  <td>
                    <div className="FP_Content text-black  no-text-wrap">
                      {blog.postedBy}
                    </div>
                  </td>
                </>
              )}
              <td>
                <FontAwesomeIcon
                  icon={faEdit}
                  onClick={() => {
                    setNewBlog(blog);
                    setEditIndex(index);
                    setIsEditing(true);
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {isEditing && (
        <div
          className="marketing-modal-overlay"
          onClick={() => {
            setIsEditing(false);
            setNewBlog({ link: "", content: "", image: images[0]?.url || "" });
          }}
        >
          <div
            className="marketing-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <CloseIcon
              className="marketing-modal-close"
              onClick={() => {
                setIsEditing(false);
                setNewBlog({
                  link: "",
                  content: "",
                  image: images[0]?.url || "",
                });
              }}
            />
            <div className="AddMarketingItemsContainer">
              <form className="marketing-add-form" onSubmit={handleSubmit}>
                <div className="fp-form-container">
                  <h3 className="FP_H3">Edit Blog</h3>
                  <NewTextArea
                    id="edit-blog-content"
                    label="Content"
                    placeholder="Content"
                    required
                    error={errors.content}
                    value={newBlog.content}
                    onChange={(event) =>
                      setNewBlog((previous) => ({
                        ...previous,
                        content: event.target.value,
                      }))
                    }
                    maxLength={10000}
                  />
                  <NewInputField
                    id="edit-blog-link"
                    label="Link"
                    placeholder="Link"
                    required
                    error={errors.link}
                    value={newBlog.link}
                    onChange={(event) =>
                      setNewBlog((previous) => ({
                        ...previous,
                        link: event.target.value,
                      }))
                    }
                  />

                  <div className="fp-form-column">
                    <CustomSelect
                      label="Select an Image"
                      required
                      options={images.map((image) => ({
                        id: image.url,
                        name: image.name,
                      }))}
                      value={newBlog.image}
                      onChange={(event) =>
                        setNewBlog((previous) => ({
                          ...previous,
                          image: event.target.value,
                        }))
                      }
                      placeholder="Select an image"
                      error={!!errors.image}
                      clearable={false}
                    />
                    {errors.image && (
                      <div className="fp-form-error FP_Content">
                        {errors.image}
                      </div>
                    )}
                  </div>

                  <div className="fp-form-actions">
                    <PrimaryButton label="Update" />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddBlog;
